import { useCategories } from "#hooks/use-categories"
import { useI18n } from "#hooks/use-i18n"
import { usePartners } from "#hooks/use-partners"
import { getCategoryBackgroundStyle } from "#lib/category-background"
import type { DashboardFilters as DashboardFiltersType } from "@home-finance-manager/shared/types/dashboard"
import { X } from "lucide-react"
import React, { useEffect, useMemo, useState } from "react"
import { Button } from "../ui/button"
import { Checkbox } from "../ui/checkbox"
import { Input } from "../ui/input"
import { Label } from "../ui/label"

interface DashboardFiltersProps {
  filters: DashboardFiltersType
  onFiltersChange: (filters: DashboardFiltersType) => void
  onApply?: () => void
  onReset?: () => void
}

const DashboardFilters: React.FC<DashboardFiltersProps> = ({
  filters,
  onFiltersChange,
  onApply,
  onReset,
}) => {
  const { t } = useI18n()
  const { data: categories = [] } = useCategories()
  const { data: partners = [] } = usePartners()

  // Local state for filters (not applied until "Apply" is clicked)
  const [localFilters, setLocalFilters] = useState<DashboardFiltersType>(filters)
  const [localSearch, setLocalSearch] = useState(filters.search || "")

  // Sync local state when filters prop changes
  useEffect(() => {
    setLocalFilters(filters)
    setLocalSearch(filters.search || "")
  }, [filters])

  const handleCategoryToggle = (categoryId: number) => {
    const currentIds = localFilters.categoryIds || []
    const newIds = currentIds.includes(categoryId)
      ? currentIds.filter((id) => id !== categoryId)
      : [...currentIds, categoryId]
    setLocalFilters({
      ...localFilters,
      categoryIds: newIds.length > 0 ? newIds : undefined,
    })
  }

  const handlePartnerToggle = (partnerId: number) => {
    const currentIds = localFilters.partnerIds || []
    const newIds = currentIds.includes(partnerId)
      ? currentIds.filter((id) => id !== partnerId)
      : [...currentIds, partnerId]
    setLocalFilters({
      ...localFilters,
      partnerIds: newIds.length > 0 ? newIds : undefined,
    })
  }

  const handleSelectAllCategories = () => {
    const allCategoryIds = categories.map((cat) => cat.id)
    setLocalFilters({
      ...localFilters,
      categoryIds: allCategoryIds,
    })
  }

  const handleDeselectAllCategories = () => {
    setLocalFilters({
      ...localFilters,
      categoryIds: undefined,
    })
  }

  const handleSelectAllPartners = () => {
    const allPartnerIds = partners.map((partner) => partner.id)
    setLocalFilters({
      ...localFilters,
      partnerIds: allPartnerIds,
    })
  }

  const handleDeselectAllPartners = () => {
    setLocalFilters({
      ...localFilters,
      partnerIds: undefined,
    })
  }

  // Check if all categories are selected
  const allCategoriesSelected = useMemo(() => {
    if (categories.length === 0) return false
    const selectedIds = localFilters.categoryIds || []
    return (
      selectedIds.length === categories.length &&
      categories.every((cat) => selectedIds.includes(cat.id))
    )
  }, [categories, localFilters.categoryIds])

  // Check if all partners are selected
  const allPartnersSelected = useMemo(() => {
    if (partners.length === 0) return false
    const selectedIds = localFilters.partnerIds || []
    return (
      selectedIds.length === partners.length &&
      partners.every((partner) => selectedIds.includes(partner.id))
    )
  }, [partners, localFilters.partnerIds])

  const handleSearchChange = (value: string) => {
    setLocalSearch(value)
    setLocalFilters({
      ...localFilters,
      search: value.trim() || undefined,
    })
  }

  const handleApply = () => {
    // Apply the filters first
    onFiltersChange(localFilters)
    // Close the popover after a brief delay to ensure state updates
    setTimeout(() => {
      onApply?.()
    }, 0)
  }

  const handleReset = () => {
    const emptyFilters: DashboardFiltersType = {}
    // Reset local state first
    setLocalFilters(emptyFilters)
    setLocalSearch("")
    // Apply empty filters immediately to update URL
    onFiltersChange(emptyFilters)
    // Close the popover/drawer after a brief delay to ensure state updates
    setTimeout(() => {
      onReset?.()
    }, 0)
  }

  return (
    <div className="space-y-6">
      {/* Search Filter */}
      <div className="space-y-2">
        <Label htmlFor="search">{t("components.dashboard.filters.search")}</Label>
        <div className="relative">
          <Input
            id="search"
            type="text"
            placeholder={t("components.dashboard.filters.searchPlaceholder")}
            value={localSearch}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pr-8"
          />
          {localSearch && (
            <button
              type="button"
              onClick={() => handleSearchChange("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Category Filter */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>{t("components.dashboard.filters.categories")}</Label>
          {categories.length > 0 && (
            <div className="flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={
                  allCategoriesSelected ? handleDeselectAllCategories : handleSelectAllCategories
                }
              >
                {allCategoriesSelected
                  ? t("components.dashboard.filters.deselectAll")
                  : t("components.dashboard.filters.selectAll")}
              </Button>
            </div>
          )}
        </div>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {categories.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("components.dashboard.filters.noCategories")}
            </p>
          ) : (
            categories.map((category) => {
              const isChecked = localFilters.categoryIds?.includes(category.id) || false
              return (
                <div
                  key={category.id}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-muted/50 p-2 rounded-md transition-colors"
                  onClick={() => handleCategoryToggle(category.id)}
                >
                  <Checkbox
                    id={`category-${category.id}`}
                    checked={isChecked}
                    onCheckedChange={() => handleCategoryToggle(category.id)}
                  />
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div
                      className="w-4 h-4 rounded flex-shrink-0"
                      style={getCategoryBackgroundStyle(category.color, category.id)}
                    />
                    <Label
                      htmlFor={`category-${category.id}`}
                      className="text-sm font-normal cursor-pointer flex-1 truncate"
                    >
                      {category.name}
                    </Label>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Partner Filter */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>{t("components.dashboard.filters.partners")}</Label>
          {partners.length > 0 && (
            <div className="flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={allPartnersSelected ? handleDeselectAllPartners : handleSelectAllPartners}
              >
                {allPartnersSelected
                  ? t("components.dashboard.filters.deselectAll")
                  : t("components.dashboard.filters.selectAll")}
              </Button>
            </div>
          )}
        </div>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {partners.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("components.dashboard.filters.noPartners")}
            </p>
          ) : (
            partners.map((partner) => {
              const isChecked = localFilters.partnerIds?.includes(partner.id) || false
              const partnerName =
                partner.firstName && partner.lastName
                  ? `${partner.firstName} ${partner.lastName}`
                  : partner.email || `Partner ${partner.id}`
              return (
                <div
                  key={partner.id}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-muted/50 p-2 rounded-md transition-colors"
                  onClick={() => handlePartnerToggle(partner.id)}
                >
                  <Checkbox
                    id={`partner-${partner.id}`}
                    checked={isChecked}
                    onCheckedChange={() => handlePartnerToggle(partner.id)}
                  />
                  <Label
                    htmlFor={`partner-${partner.id}`}
                    className="text-sm font-normal cursor-pointer flex-1"
                  >
                    {partnerName}
                  </Label>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 pt-2 border-t">
        <Button variant="outline" onClick={handleReset} className="flex-1" size="sm">
          {t("components.dashboard.filters.reset")}
        </Button>
        <Button onClick={handleApply} className="flex-1" size="sm">
          {t("components.dashboard.filters.apply")}
        </Button>
      </div>
    </div>
  )
}

export default DashboardFilters
